// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Notification {
  id: number;
  title: string;
  message: string;
  created_at: string;
  is_read: boolean;
}

interface NotificationResponse {
  notifications: Notification[];
  unread_count: number;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const NotificationBell: React.FC = () => {
  const { isDark } = useTheme();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/notifications`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data: NotificationResponse = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;

    try {
      await fetch(`${API_BASE}/api/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(unreadIds)
      });

      // Update local state
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  // Fetch on mount and every 5 minutes
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      fetchNotifications(); // Refresh when opening
      // Mark all as read after a short delay (so user sees what's new)
      setTimeout(() => {
        markAllAsRead();
      }, 1000);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={toggleDropdown}
        className={`relative p-2 rounded-xl transition-all duration-200 ${
          isDark
            ? 'border border-white/10 hover:bg-white/10'
            : 'border border-gray-200 hover:bg-gray-100'
        }`}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full animate-pulse"
            style={{
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}
          ></span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-80 rounded-xl shadow-lg border overflow-hidden ${
            isDark
              ? 'bg-zinc-900 border-white/10'
              : 'bg-white border-gray-200'
          }`}
          style={{ zIndex: 1000 }}
        >
          {/* Header */}
          <div
            className={`px-4 py-3 border-b ${
              isDark ? 'border-white/10' : 'border-gray-200'
            }`}
          >
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Notifications
            </h3>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center">
                <div
                  className={`text-sm ${
                    isDark ? 'text-white/50' : 'text-gray-500'
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell
                  className={`h-12 w-12 mx-auto mb-2 ${
                    isDark ? 'text-white/20' : 'text-gray-300'
                  }`}
                />
                <div
                  className={`text-sm ${
                    isDark ? 'text-white/50' : 'text-gray-500'
                  }`}
                >
                  No notifications yet
                </div>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b last:border-b-0 transition-colors ${
                      isDark
                        ? 'border-white/10 hover:bg-white/5'
                        : 'border-gray-200 hover:bg-gray-50'
                    } ${!notification.is_read ? (isDark ? 'bg-blue-500/5' : 'bg-blue-50') : ''}`}
                  >
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        {!notification.is_read && (
                          <span className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                        )}
                        <h4
                          className={`font-semibold text-sm ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {notification.title}
                        </h4>
                      </div>
                      <span
                        className={`text-xs whitespace-nowrap ${
                          isDark ? 'text-white/50' : 'text-gray-500'
                        }`}
                      >
                        {formatDate(notification.created_at)}
                      </span>
                    </div>
                    <p
                      className={`text-sm ${
                        isDark ? 'text-white/70' : 'text-gray-600'
                      }`}
                    >
                      {notification.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add keyframe animation for pulse */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;
